import { memo } from 'react';
import { PresenceDot, cn, type PresenceDotProps } from '@cyphex/ui';
import { presenceTitle } from '../lastSeen';
import { ACTIVITY_META } from '../presenceMeta';
import type { ActivityState } from '../types';

export interface PresenceIndicatorProps {
  state: ActivityState;
  size?: PresenceDotProps['size'];
  /**
   * Epoch ms this person was last actually here. A bare dot has no room for a
   * line, so it carries the same sentence `PresenceLine` would have written in
   * its accessible name and tooltip instead of leaving colour to say it alone.
   */
  lastActivityAt?: number | null;
  /** Renders the state name beside the dot. */
  showLabel?: boolean;
  /** Custom status "🎧 Focus time" appended after the state name. */
  statusEmoji?: string | null;
  statusText?: string | null;
  /** Ring colour must match the surface behind the dot. */
  ringClassName?: string;
  /** Pulse for a ringing / speaking treatment. */
  pulse?: boolean;
  className?: string;
}

/**
 * Presence dot + optional label for the full activity vocabulary.
 *
 * `ActivityState` is richer than the design-system `PresenceState` (it splits
 * On a call / Presenting / Screen sharing), so the dot colour comes from the
 * mapped Wave-1 token while the label carries the extra meaning — presence is
 * never colour-only.
 *
 * The dot already draws the state glyph (tick / dash / clock / cross), so the
 * labelled form does NOT repeat it beside the text: "✓ ✓ Available" reads as
 * two separate claims about one fact.
 */
export const PresenceIndicator = memo(function PresenceIndicator({
  state, size = 'md', lastActivityAt = null, showLabel = false, statusEmoji, statusText,
  ringClassName, pulse = false, className,
}: PresenceIndicatorProps) {
  const meta = ACTIVITY_META[state];

  if (!showLabel) {
    return (
      <PresenceDot
        state={meta.dot}
        /* The dot IS the whole affordance here, so it says the whole thing. */
        label={presenceTitle(state, lastActivityAt)}
        size={size}
        pulse={pulse}
        ringClassName={ringClassName}
        className={className}
      />
    );
  }

  return (
    <span className={cn('inline-flex min-w-0 items-center gap-1.5', className)}>
      <PresenceDot state={meta.dot} size={size} pulse={pulse} ringClassName={ringClassName} />
      <span className="truncate text-md font-medium text-secondary-foreground">{meta.label}</span>
      {statusText ? (
        <span className="truncate text-sm text-muted-foreground">
          {statusEmoji ? `${statusEmoji} ` : ''}
          {statusText}
        </span>
      ) : null}
    </span>
  );
});
